import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { format, getDay, parse, startOfDay, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { CircleArrowLeft, Loader2Icon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { useGetClassCalendar } from '~entities/classes/api/get-class-calender';
import { useGetAllCalendarSessions } from '~entities/session/api/get-all-calendar-sessions';
import { useGetTrialBookedCounts } from '~entities/student/api/get-trial-booked-counts';

import {
  filterClassCalendarEvents,
  filterEventsWithBookedTrials,
} from '~features/classes/lib/class-calendar-filters';
import { BookingLinkDialog } from '~features/classes/ui/booking-link-dialog';
import { ClassDayFilter } from '~features/classes/ui/class-day-filter';
import { ClassOptionFilter } from '~features/classes/ui/class-option-filter';
import { ClassTrialBookingsDialog } from '~features/classes/ui/class-trial-bookings-dialog';
import '~features/events/ui/event-calendar.css';
import {
  getEventCalendarRange,
  isEventCalendarView,
} from '~features/events/ui/event-calendar.helpers';
import {
  DESKTOP_EVENT_CALENDAR_VIEWS,
  EVENT_CALENDAR_VIEW,
  type EventCalendarItem,
  type EventCalendarView,
} from '~features/events/ui/event-calendar.types';
import { MobileEventCalendar } from '~features/events/ui/mobile-event-calendar';
import { StudentSelectorDialog } from '~features/new-dashboard/ui/student-selector-dialog';
import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';

import {
  EnrollioTableDateFilter,
  EnrollioTableFilters,
  EnrollioTableToggleFilter,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const today = startOfDay(new Date());

interface ClassCalendarEvent extends EventCalendarItem {
  eventKey: string;
  classTitle: string;
  occurrenceDay: string;
  spotsRemaining: number | null;
  sessionId: string | null;
  sessionName: string;
}

function uniqueOptions(
  events: Array<{ id?: string | null; label?: string }>,
): Array<{ id: string; label: string }> {
  const byId = new Map<string, string>();
  for (const event of events) {
    if (!event.id || !event.label) continue;
    byId.set(event.id, event.label);
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function ClassCalendar() {
  const router = useRouter();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [view, setView] = useState<EventCalendarView>(EVENT_CALENDAR_VIEW.WEEK);
  const [date, setDate] = useState(today);
  const [query, setQuery] = useState('');
  const [locationId, setLocationId] = useState('All');
  const [sessionId, setSessionId] = useState('All');
  const [day, setDay] = useState('All');
  const [age, setAge] = useState('');
  const [spots, setSpots] = useState<'all' | 'open' | 'full'>('all');
  const [trialsOnly, setTrialsOnly] = useState(false);
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const [roster, setRoster] = useState<ClassCalendarEvent | null>(null);
  const [bookingLinkClassId, setBookingLinkClassId] = useState<string | null>(
    null,
  );
  const [trialBookingEvent, setTrialBookingEvent] =
    useState<ClassCalendarEvent | null>(null);
  const [studentSelectorOpen, setStudentSelectorOpen] = useState(false);
  const [enrollmentStudentId, setEnrollmentStudentId] = useState<string | null>(
    null,
  );
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);

  const range = getEventCalendarRange(view, date);
  const { data, isLoading, isFetching } = useGetClassCalendar({
    startDate: format(range.start, 'yyyy-MM-dd'),
    endDate: format(range.end, 'yyyy-MM-dd'),
  });
  const {
    data: sessionsData,
    fetchNextPage: fetchNextSessionPage,
    hasNextPage: hasNextSessionPage,
    isFetchingNextPage: isFetchingNextSessionPage,
  } = useGetAllCalendarSessions();
  useEffect(() => {
    if (hasNextSessionPage && !isFetchingNextSessionPage) {
      void fetchNextSessionPage();
    }
  }, [fetchNextSessionPage, hasNextSessionPage, isFetchingNextSessionPage]);
  const sourceEvents = useMemo(() => data?.data ?? [], [data?.data]);
  const matchingEvents = useMemo(
    () =>
      filterClassCalendarEvents(sourceEvents, {
        query,
        locationId,
        sessionId,
        day: view === EVENT_CALENDAR_VIEW.DAY ? 'All' : day,
        age,
        spots,
      }),
    [sourceEvents, query, locationId, sessionId, day, age, spots, view],
  );
  const classIds = [...new Set(matchingEvents.map((item) => item.resourceId))];
  const { data: trialCounts, isFetching: isFetchingTrialCounts } =
    useGetTrialBookedCounts(classIds);
  const filteredEvents = useMemo(
    () =>
      filterEventsWithBookedTrials(
        matchingEvents,
        trialCounts?.byOccurrence,
        trialsOnly,
      ),
    [matchingEvents, trialCounts?.byOccurrence, trialsOnly],
  );
  const locationOptions = useMemo(
    () =>
      uniqueOptions(
        sourceEvents.map((event) => ({
          id: event.locationId,
          label: event.locationName,
        })),
      ),
    [sourceEvents],
  );
  const sessionOptions = useMemo(() => {
    const fromSessions =
      sessionsData?.pages.flatMap((page) =>
        page.sessions.map((session) => ({
          id: session._id,
          label: session.name,
        })),
      ) ?? [];
    return uniqueOptions([
      ...fromSessions,
      ...sourceEvents.map((event) => ({
        id: event.sessionId,
        label: event.sessionName,
      })),
    ]);
  }, [sessionsData?.pages, sourceEvents]);
  const hasActiveFilters =
    query.trim().length > 0 ||
    locationId !== 'All' ||
    sessionId !== 'All' ||
    day !== 'All' ||
    age.trim().length > 0 ||
    spots !== 'all' ||
    trialsOnly;

  const clearFilters = () => {
    setQuery('');
    setLocationId('All');
    setSessionId('All');
    setDay('All');
    setAge('');
    setSpots('all');
    setTrialsOnly(false);
  };

  const showCalendarRefresh =
    (isFetching && data != null) || (trialsOnly && isFetchingTrialCounts);

  if (isLoading) {
    return (
      <div className='flex h-[calc(100vh-10rem)] items-center justify-center'>
        <Loader2Icon className='animate-spin' />
      </div>
    );
  }

  const calendarEvents: Array<ClassCalendarEvent> = filteredEvents.map(
    (classEvent) => {
      const occurrenceDay = classEvent.start.slice(0, 10);
      const trials =
        trialCounts?.byOccurrence[
          `${classEvent.resourceId}|${occurrenceDay}`
        ] ?? 0;
      const spotsLabel =
        classEvent.spotsRemaining == null
          ? ''
          : ` · ${classEvent.spotsRemaining} spots`;
      const eventKey = `${classEvent.resourceId}|${classEvent.start}`;
      return {
        title: `${classEvent.title}${spotsLabel} · ${trials} trials`,
        start: new Date(classEvent.start),
        end: new Date(classEvent.end),
        resourceId: classEvent.resourceId,
        color: classEvent.color,
        days: classEvent.day ? [classEvent.day] : [],
        eventKey,
        classTitle: classEvent.title,
        occurrenceDay,
        spotsRemaining: classEvent.spotsRemaining ?? null,
        sessionId: classEvent.sessionId ?? null,
        sessionName: classEvent.sessionName ?? '',
      };
    },
  );
  const selectedEvent =
    calendarEvents.find((event) => event.eventKey === selectedEventKey) ?? null;

  return (
    <div className='relative min-w-0'>
      <div className='border-b px-4 py-3'>
        <div className='flex min-w-0 items-center gap-2'>
          <Button
            variant='ghost'
            className='flex shrink-0 items-center p-2 [&_svg]:size-6'
            disabled={!canGoBack}
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
          </Button>
          <h2 className='mr-2 shrink-0 text-lg font-semibold'>
            Class Calendar
          </h2>
        </div>
        <EnrollioTableFilters className='mb-0 mt-3'>
          <Input
            value={query}
            placeholder='Search classes'
            className='w-full md:w-44'
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <ClassOptionFilter
            value={locationId}
            onChange={setLocationId}
            options={locationOptions.map((option) => ({
              value: option.id,
              label: option.label,
            }))}
            allLabel='All locations'
            searchPlaceholder='Search locations'
            className='h-9 w-full justify-start md:w-44'
          />
          <ClassOptionFilter
            value={sessionId}
            onChange={setSessionId}
            options={sessionOptions.map((option) => ({
              value: option.id,
              label: option.label,
            }))}
            allLabel='All sessions'
            searchPlaceholder='Search sessions'
            className='h-9 w-full justify-start md:w-44'
          />
          <div
            className={
              view === EVENT_CALENDAR_VIEW.DAY
                ? 'pointer-events-none opacity-50'
                : ''
            }
            title={
              view === EVENT_CALENDAR_VIEW.DAY
                ? 'Day filtering is not used in Day view'
                : undefined
            }
          >
            <ClassDayFilter
              value={view === EVENT_CALENDAR_VIEW.DAY ? 'All' : day}
              onChange={setDay}
            />
          </div>
          <Input
            value={age}
            inputMode='numeric'
            placeholder='Age'
            className='w-full md:w-20'
            onChange={(event) => {
              setAge(event.target.value);
            }}
          />
          <select
            className='h-9 w-full rounded-md border bg-background px-2 text-sm md:w-auto'
            value={spots}
            onChange={(event) => {
              const value = event.target.value;
              if (value === 'open' || value === 'full' || value === 'all') {
                setSpots(value);
              }
            }}
          >
            <option value='all'>All spots</option>
            <option value='open'>Available spots</option>
            <option value='full'>Full</option>
          </select>
          <EnrollioTableDateFilter
            id='class-calendar-date'
            label='Select date'
            value={format(date, 'yyyy-MM-dd')}
            onChange={(value) => {
              setDate(parse(value, 'yyyy-MM-dd', new Date()));
            }}
          />
          <EnrollioTableToggleFilter
            id='class-calendar-trials-only'
            label='Trials only'
            checked={trialsOnly}
            onChange={setTrialsOnly}
          />
          {hasActiveFilters ? (
            <Button
              type='button'
              variant='outline'
              className='h-9 shrink-0'
              onClick={clearFilters}
            >
              Clear all
            </Button>
          ) : null}
        </EnrollioTableFilters>
      </div>
      <div className='event-calendar relative min-w-0 p-4 sm:h-[calc(100vh-8rem)]'>
        {showCalendarRefresh ? (
          <div
            className='absolute inset-0 z-20 flex items-center justify-center bg-background/60'
            aria-busy='true'
            aria-live='polite'
          >
            <Loader2Icon className='size-8 animate-spin' />
          </div>
        ) : null}
        <div className='md:hidden'>
          <MobileEventCalendar
            view={view}
            date={date}
            events={calendarEvents}
            onViewChange={setView}
            onDateChange={setDate}
          />
        </div>
        <div className='hidden h-full overflow-x-auto md:block'>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor='start'
            endAccessor='end'
            date={date}
            view={view}
            views={DESKTOP_EVENT_CALENDAR_VIEWS}
            selected={selectedEvent ?? undefined}
            onSelectEvent={(event) => {
              setSelectedEventKey(event.eventKey);
              setRoster(event);
            }}
            onNavigate={setDate}
            onView={(nextView) => {
              if (isEventCalendarView(nextView)) {
                setView(nextView);
              }
            }}
            scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: event.color,
                border: '1px solid rgba(15, 23, 42, 0.45)',
                borderRadius: '4px',
                color: 'black',
                zIndex: event.eventKey === selectedEventKey ? 30 : 1,
              },
            })}
          />
        </div>
      </div>
      {roster ? (
        <ClassTrialBookingsDialog
          classId={roster.resourceId}
          className={roster.classTitle}
          open
          onOpenChange={(open) => {
            if (!open) setRoster(null);
          }}
          onViewClass={() => {
            const classId = roster.resourceId;
            setRoster(null);
            window.setTimeout(() => {
              void navigate({
                to: '/classes/$classId/enrolled-students',
                params: { classId },
              });
            }, 0);
          }}
          onBookTrial={() => {
            setTrialBookingEvent(roster);
            setRoster(null);
            setStudentSelectorOpen(true);
          }}
          onConfigureBookingLink={() => {
            setBookingLinkClassId(roster.resourceId);
            setRoster(null);
          }}
        />
      ) : null}
      <StudentSelectorDialog
        open={studentSelectorOpen}
        onOpenChange={setStudentSelectorOpen}
        onEnroll={(studentId) => {
          if (!studentId) return;
          setEnrollmentStudentId(studentId);
          setStudentSelectorOpen(false);
          setEnrollmentOpen(true);
        }}
      />
      {trialBookingEvent && enrollmentStudentId ? (
        <EnrollStudentModal
          studentId={enrollmentStudentId}
          dialogOpen={enrollmentOpen}
          setDialogOpen={setEnrollmentOpen}
          initialEnrollmentType='trial'
          initialTrialDate={trialBookingEvent.occurrenceDay}
          preSelectedSession={
            trialBookingEvent.sessionId
              ? {
                  value: trialBookingEvent.sessionId,
                  label: trialBookingEvent.sessionName || 'Class session',
                }
              : null
          }
          preSelectedClass={{
            value: trialBookingEvent.resourceId,
            label: trialBookingEvent.classTitle,
          }}
          onTrialEnrollmentSuccess={() => {
            setEnrollmentOpen(false);
            setEnrollmentStudentId(null);
            setTrialBookingEvent(null);
          }}
        />
      ) : null}
      {bookingLinkClassId ? (
        <BookingLinkDialog
          open
          classId={bookingLinkClassId}
          onOpenChange={(open) => {
            if (!open) setBookingLinkClassId(null);
          }}
          onEdit={() => {
            const classId = bookingLinkClassId;
            setBookingLinkClassId(null);
            // Close the Radix dialog before changing routes so its modal
            // overlay cannot leave the destination page inert.
            window.setTimeout(() => {
              void navigate({
                to: '/classes/$classId',
                params: { classId },
              });
            }, 0);
          }}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/_auth/class-calendar')({
  component: ClassCalendar,
});
